import React, { useState, useCallback } from 'react';
import { Button, Checkbox } from '@jjj/components';
import type { CheckboxChangeEvent } from 'src/interfaces/antd';
import { t } from '../../../../i18n';
import { ISwitchModalTarget } from '../OrgSwitcher';
import { ROLE_IDS } from '../../../../redux/orgSwitcher/types';
import { openUnifiedLogin, redirectToUnifiedLogin, disableSafeguard } from '../utils';
import { StyledModal, ModalDescription, ModalFooter, CheckboxWrapper } from './SwitchOrgModal.style';

interface ISwitchOrgModalProps {
  visible: boolean;
  target: ISwitchModalTarget | null;
  userEmail: string;
  onClose: () => void;
}

const SwitchOrgModal: React.FC<ISwitchOrgModalProps> = ({ visible, target, userEmail, onClose }) => {
  const [dontAskAgain, setDontAskAgain] = useState(false);

  const handleCancel = useCallback(() => {
    onClose();
    setDontAskAgain(false);
  }, [onClose]);

  const handleConfirm = useCallback(() => {
    if (dontAskAgain) {
      disableSafeguard();
    }

    if (target) {
      const params = { email: userEmail, orgId: target.orgId, role: target.roleId };

      if (target.roleId === ROLE_IDS.admin) {
        redirectToUnifiedLogin(params);
      } else {
        openUnifiedLogin(params);
      }
    }

    setDontAskAgain(false);
    onClose();
  }, [dontAskAgain, target, userEmail, onClose]);

  const handleCheckboxChange = useCallback((e: CheckboxChangeEvent) => {
    setDontAskAgain(e.target.checked);
  }, []);

  return (
    <StyledModal
      title={t('dash.topbar.org_switcher.switch_modal_title')}
      open={visible}
      onCancel={handleCancel}
      closable={false}
      footer={
        <ModalFooter>
          <Button type="secondary" onClick={handleCancel}>
            {t('dash.topbar.org_switcher.cancel_button')}
          </Button>
          <Button type="primary" onClick={handleConfirm}>
            {t('dash.topbar.org_switcher.confirm_button')}
          </Button>
        </ModalFooter>
      }
      width={400}
      data-cy="org-switcher-modal"
    >
      <ModalDescription>{t('dash.topbar.org_switcher.switch_modal_message')}</ModalDescription>

      <CheckboxWrapper>
        <Checkbox checked={dontAskAgain} onChange={handleCheckboxChange} data-cy="org-switcher-dont-ask-checkbox">
          {t('dash.topbar.org_switcher.dont_ask_again')}
        </Checkbox>
      </CheckboxWrapper>
    </StyledModal>
  );
};

export default SwitchOrgModal;
